import React, { useState, useEffect, useRef } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Form, FormControl, FormDescription, FormField, FormItem, FormLabel, FormMessage } from '@/components/ui/form';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Textarea } from '@/components/ui/textarea';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Loader2, Stethoscope, AlertCircle, Calendar, FileText } from 'lucide-react';
import { Procedure, ProcedureData, ProcedureTypeReference } from '@/types/procedure.types';
import { procedureService } from '@/services/procedureService';
import { ProcedureCatalogAutocomplete } from '@/components/common/ProcedureCatalogAutocomplete';
import { Patient } from '@/types/patient.types';
import { useToast } from '@/hooks/use-toast';
import { format } from 'date-fns';
import { ro } from 'date-fns/locale';

// Validation schema for new API structure
const procedureSchema = z.object({
  patient_id: z.number().min(1, 'ID pacient este obligatoriu'),
  procedure_type_id: z.number().min(1, 'Tipul procedurii este obligatoriu'),
  procedure_catalog_id: z.number().optional(),
  procedure_name: z.string().optional(),
  procedure_date: z.string().min(1, 'Data procedurii este obligatorie'),
  outcome_30d: z.union([z.string(), z.number()]).optional(),
  complications: z.string().optional(),
  reintervention_1y: z.union([z.string(), z.number()]).optional(),
  performed_by_user_id: z.number().optional(),
  notes: z.string().optional(), // Brief admin notes
  note: z.string().optional(),  // Detailed doctor notes
});

type ProcedureFormData = z.infer<typeof procedureSchema>;

interface ProcedureFormProps {
  patient: Patient;
  procedure?: Procedure | null;
  onSubmit: (data: ProcedureData) => Promise<void>;
  onCancel: () => void;
}

export const ProcedureForm: React.FC<ProcedureFormProps> = ({
  patient,
  procedure,
  onSubmit,
  onCancel,
}) => {
  const { toast } = useToast();
  const [isLoading, setIsLoading] = useState(false);
  const [procedureTypes, setProcedureTypes] = useState<ProcedureTypeReference[]>([]);
  const [loadingTypes, setLoadingTypes] = useState(true);
  const [outcomeOptions, setOutcomeOptions] = useState<Array<{value: string, label: string}>>([]);
  const [reinterventionOptions, setReinterventionOptions] = useState<Array<{value: string, label: string}>>([]);

  const form = useForm<ProcedureFormData>({
    resolver: zodResolver(procedureSchema),
    defaultValues: {
      patient_id: patient.id,
      procedure_type_id: procedure?.procedure_type_id || undefined,
      procedure_catalog_id: procedure?.procedure_catalog_id || undefined,
      procedure_name: procedure?.procedure_name || '',
      procedure_date: procedure?.procedure_date || '',
      outcome_30d: procedure?.outcome_30d || undefined,
      complications: procedure?.complications || '',
      reintervention_1y: procedure?.reintervention_1y || undefined,
      performed_by_user_id: procedure?.performed_by_user_id || undefined,
      notes: procedure?.notes || '',
      note: procedure?.note || '',
    },
  });

  // Load all necessary data from API
  useEffect(() => {
    const loadFormData = async () => {
      try {
        setLoadingTypes(true);
        
        // Load procedure types, outcome options, and reintervention options in parallel
        const [types, outcomes, reinterventions] = await Promise.all([
          procedureService.getProcedureTypes(),
          procedureService.getOutcomeOptions().catch(() => []), // Graceful fallback
          procedureService.getReinterventionOptions().catch(() => []) // Graceful fallback
        ]);
        
        console.log('Loaded procedure types:', types);
        setProcedureTypes(types);
        setOutcomeOptions(outcomes);
        setReinterventionOptions(reinterventions);
        
      } catch (error: any) {
        console.error('Failed to load form data:', error);
        toast({
          title: 'Atenție',
          description: 'Nu s-au putut încărca datele pentru formular.',
          variant: 'destructive',
        });
        // Set fallback empty arrays
        setProcedureTypes([]);
        setOutcomeOptions([]);
        setReinterventionOptions([]);
      } finally {
        setLoadingTypes(false);
      }
    };

    loadFormData();
  }, [toast]);

  // Reset the catalog selection when "Tip procedură" changes after the
  // initial load, since the catalog picker is filtered by procedure_type_id
  // (a surgery pick doesn't make sense once switched to catheterization).
  const watchedTypeId = form.watch('procedure_type_id');
  const previousTypeIdRef = useRef<number | undefined>(procedure?.procedure_type_id);
  useEffect(() => {
    if (previousTypeIdRef.current !== undefined && previousTypeIdRef.current !== watchedTypeId) {
      form.setValue('procedure_catalog_id', undefined);
      form.setValue('procedure_name', '');
    }
    previousTypeIdRef.current = watchedTypeId;
  }, [watchedTypeId]); // eslint-disable-line react-hooks/exhaustive-deps

  // Validate procedure date is not before birth date
  const validateProcedureDate = (date: string): boolean => {
    if (!date || !patient.birth_date) return true;
    const procedureDate = new Date(date);
    const birthDate = new Date(patient.birth_date);
    return procedureDate >= birthDate;
  };

  const handleSubmit = async (data: ProcedureFormData) => {
    try {
      // Validate procedure date
      if (!validateProcedureDate(data.procedure_date)) {
        toast({
          title: 'Eroare',
          description: 'Data procedurii nu poate fi înainte de data nașterii pacientului',
          variant: 'destructive',
        });
        return;
      }

      setIsLoading(true);
      await onSubmit(data as ProcedureData);
      
      toast({
        title: 'Succes',
        description: procedure 
          ? 'Procedura a fost actualizată cu succes'
          : 'Procedura a fost adăugată cu succes',
      });
    } catch (error: any) {
      toast({
        title: 'Eroare',
        description: error.message || 'A apărut o eroare la salvarea procedurii',
        variant: 'destructive',
      });
    } finally {
      setIsLoading(false);
    }
  };


  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(handleSubmit)} className="space-y-6">
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Stethoscope className="h-5 w-5" />
              {procedure ? 'Editează procedura' : 'Adaugă procedură nouă'}
            </CardTitle>
            <CardDescription>
              Pacient: <strong>{patient.name}</strong> (Cod: {patient.patient_code})
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {/* Alert for birth date validation */}
            {patient.birth_date && (
              <Alert>
                <AlertCircle className="h-4 w-4" />
                <AlertDescription>
                  Data procedurii trebuie să fie după {format(new Date(patient.birth_date), 'dd MMMM yyyy', { locale: ro })}
                </AlertDescription>
              </Alert>
            )}

            <div className="grid grid-cols-2 gap-4">
              {/* Procedure Type */}
              <FormField
                control={form.control}
                name="procedure_type_id"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Tip procedură *</FormLabel>
                    <Select 
                      onValueChange={(value) => field.onChange(parseInt(value))}
                      value={field.value?.toString() || ''}
                    >
                      <FormControl>
                        <SelectTrigger>
                          <SelectValue placeholder="Selectați tipul" />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        {loadingTypes ? (
                          <SelectItem value="loading" disabled>Se încarcă tipurile...</SelectItem>
                        ) : procedureTypes.length > 0 ? (
                          procedureTypes.map((type) => (
                            <SelectItem key={type.id} value={type.id.toString()}>
                              {type.name_ro}
                            </SelectItem>
                          ))
                        ) : (
                          <SelectItem value="no-types" disabled>Nu sunt tipuri disponibile</SelectItem>
                        )}
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )}
              />

              {/* Procedure Date */}
              <FormField
                control={form.control}
                name="procedure_date"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Data procedurii *</FormLabel>
                    <FormControl>
                      <div className="relative">
                        <Calendar className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                        <Input 
                          type="date" 
                          className="pl-10"
                          min={patient.birth_date}
                          max={new Date().toISOString().split('T')[0]}
                          {...field} 
                        />
                      </div>
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>


            {/* Procedure Name */}
            <FormField
              control={form.control}
              name="procedure_name"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Nume procedură</FormLabel>
                  <FormControl>
                    <ProcedureCatalogAutocomplete
                      procedureTypeId={form.watch('procedure_type_id')}
                      catalogId={form.watch('procedure_catalog_id')}
                      textValue={field.value}
                      onSelect={(item) => {
                        form.setValue('procedure_catalog_id', item.id);
                        field.onChange(item.name_ro);
                      }}
                      onFreeText={(name) => {
                        form.setValue('procedure_catalog_id', undefined);
                        field.onChange(name);
                      }}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <div className="grid grid-cols-2 gap-4">
              {/* Outcome 30 days */}
              <FormField
                control={form.control}
                name="outcome_30d"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Rezultat la 30 zile</FormLabel>
                    <Select onValueChange={field.onChange} value={field.value?.toString() || ''}>
                      <FormControl>
                        <SelectTrigger>
                          <SelectValue placeholder="Selectați rezultatul" />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        {outcomeOptions.length > 0 ? (
                          outcomeOptions.map((option) => (
                            <SelectItem key={option.value} value={option.value}>
                              {option.label}
                            </SelectItem>
                          ))
                        ) : (
                          <>
                            <SelectItem value="success">Succes</SelectItem>
                            <SelectItem value="complications">Complicații</SelectItem>
                            <SelectItem value="death">Deces</SelectItem>
                          </>
                        )}
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )}
              />

              {/* Reintervention 1 year */}
              <FormField
                control={form.control}
                name="reintervention_1y"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Reintervenție la 1 an</FormLabel>
                    <Select onValueChange={field.onChange} value={field.value?.toString() || ''}>
                      <FormControl>
                        <SelectTrigger>
                          <SelectValue placeholder="Selectați opțiunea" />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        {reinterventionOptions.length > 0 ? (
                          reinterventionOptions.map((option) => (
                            <SelectItem key={option.value} value={option.value}>
                              {option.label}
                            </SelectItem>
                          ))
                        ) : (
                          <>
                            <SelectItem value="none">Nu</SelectItem>
                            <SelectItem value="required">Da</SelectItem>
                            <SelectItem value="unknown">Necunoscut</SelectItem>
                          </>
                        )}
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            {/* Complications */}
            <FormField
              control={form.control}
              name="complications"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Complicații</FormLabel>
                  <FormControl>
                    <Textarea 
                      placeholder="Descrieți orice complicații apărute..."
                      className="min-h-[80px]"
                      {...field} 
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <div className="grid grid-cols-1 gap-4">
              {/* Brief Admin Notes */}
              <FormField
                control={form.control}
                name="notes"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Observații administrative</FormLabel>
                    <FormControl>
                      <Textarea 
                        placeholder="Note scurte administrative..."
                        className="min-h-[80px]"
                        {...field} 
                      />
                    </FormControl>
                    <FormDescription>
                      Note scurte pentru administrare și coordonare
                    </FormDescription>
                    <FormMessage />
                  </FormItem>
                )}
              />

              {/* Detailed Doctor Notes */}
              <FormField
                control={form.control}
                name="note"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Note medicale detaliate</FormLabel>
                    <FormControl>
                      <Textarea 
                        placeholder="Note medicale detaliate despre procedură, rezultate, observații clinice..."
                        className="min-h-[120px]"
                        {...field} 
                      />
                    </FormControl>
                    <FormDescription>
                      Note detaliate ale medicului despre procedură
                    </FormDescription>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>
          </CardContent>
        </Card>

        {/* Form Actions - sticky so the submit button stays reachable when the
            dialog content is taller than the viewport and scrolls internally */}
        <div className="sticky bottom-0 -mx-6 -mb-6 flex justify-end gap-4 border-t bg-card px-6 py-4">
          <Button type="button" variant="outline" onClick={onCancel}>
            Anulează
          </Button>
          <Button type="submit" disabled={isLoading}>
            {isLoading ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Se salvează...
              </>
            ) : (
              <>
                <FileText className="mr-2 h-4 w-4" />
                {procedure ? 'Actualizează' : 'Adaugă'} procedura
              </>
            )}
          </Button>
        </div>
      </form>
    </Form>
  );
};